import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessEventContext = createContext();

export const useBusinessEvent = () => {
  const context = useContext(BusinessEventContext);
  if (!context) {
    throw new Error('useBusinessEvent must be used within a BusinessEventProvider');
  }
  return context;
};

export const BusinessEventProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for business event endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';
  
  // Determine the correct base URL based on login type
  const getEventBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/business-events/api/v1/business-events`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/business-events/api/v1/business-events`;
    } else {
      throw new Error('Invalid login type for business events access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    
    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access business events');
    }
  }, [loginType]);

  // ========================
  // CREATE OPERATION (POST)
  // ========================
  
  /**
   * Create a new business event
   * @param {Object} eventData - Event data to create
   * @returns {Promise<Object>} Created event object
   */
  const createEvent = useCallback(async (eventData) => {
    validateAccess();
    
    if (!eventData) {
      throw new Error('Event data is required');
    }

    // Validate required fields
    const requiredFields = ['business_registry_number', 'event_title', 'event_date', 'event_time', 'event_status'];
    const missingFields = requiredFields.filter(field => !eventData[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(eventData.event_date)) {
      throw new Error('Event date must be in YYYY-MM-DD format');
    }

    // Validate time format (HH:MM:SS)
    const timeRegex = /^\d{2}:\d{2}:\d{2}$/;
    if (!timeRegex.test(eventData.event_time)) {
      throw new Error('Event time must be in HH:MM:SS format');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getEventBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventData),
      });
      
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to create business event';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getEventBaseUrl]);

  // ========================
  // UPDATE OPERATION (PUT)
  // ========================
  
  /**
   * Update an existing business event
   * @param {string} eventId - Event ID to update
   * @param {Object} updateData - Partial event data to update
   * @returns {Promise<Object>} Updated event object
   */
  const updateEvent = useCallback(async (eventId, updateData) => {
    validateAccess();
    
    if (!eventId) {
      throw new Error('Event ID is required');
    }

    if (!updateData || Object.keys(updateData).length === 0) {
      throw new Error('Update data is required');
    }

    // Validate date format if provided
    if (updateData.event_date) {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(updateData.event_date)) {
        throw new Error('Event date must be in YYYY-MM-DD format');
      }
    }

    // Validate time format if provided
    if (updateData.event_time) {
      const timeRegex = /^\d{2}:\d{2}:\d{2}$/;
      if (!timeRegex.test(updateData.event_time)) {
        throw new Error('Event time must be in HH:MM:SS format');
      }
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getEventBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/event/${encodeURIComponent(eventId)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });
      
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update business event';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getEventBaseUrl]);

  // ========================
  // DELETE OPERATION (DELETE)
  // ========================
  
  /**
   * Delete a business event
   * @param {string} eventId - Event ID to delete
   * @returns {Promise<Object>} Deletion confirmation
   */
  const deleteEvent = useCallback(async (eventId) => {
    validateAccess();
    
    if (!eventId) {
      throw new Error('Event ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getEventBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/event/${encodeURIComponent(eventId)}`, {
        method: 'DELETE',
      });
      
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete business event';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getEventBaseUrl]);

  // ========================
  // READ OPERATIONS (GET)
  // ========================

  // Get events by registry number with pagination and filters
  const getEventsByRegistryNumber = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);
      if (options.eventStatus) queryParams.append('event_status', options.eventStatus);
      if (options.eventVisible !== undefined) queryParams.append('event_visible', options.eventVisible);
      if (options.dateFrom) queryParams.append('date_from', options.dateFrom);
      if (options.dateTo) queryParams.append('date_to', options.dateTo);
      if (options.minPrice) queryParams.append('min_price', options.minPrice);
      if (options.maxPrice) queryParams.append('max_price', options.maxPrice);
      if (options.hasLocation !== undefined) queryParams.append('has_location', options.hasLocation);
      if (options.hasGuestLimit !== undefined) queryParams.append('has_guest_limit', options.hasGuestLimit);
      if (options.search) queryParams.append('search', options.search);

      const baseUrl = getEventBaseUrl();
      const url = `${baseUrl}/registry/${encodeURIComponent(registryNumber)}`;
      const fullUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;
      
      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch business events';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getEventBaseUrl]);

  // Get event statistics by registry number
  const getEventStats = useCallback(async (registryNumber) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getEventBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}/stats`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get event statistics';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getEventBaseUrl]);

  // Get event statuses by registry number
  const getEventStatuses = useCallback(async (registryNumber) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getEventBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}/statuses`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get event statuses';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getEventBaseUrl]);

  // Get event by event ID
  const getEventById = useCallback(async (eventId) => {
    validateAccess();
    
    if (!eventId) {
      throw new Error('Event ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getEventBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/event/${encodeURIComponent(eventId)}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get event by ID';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getEventBaseUrl]);

  // ========================
  // CONVENIENCE METHODS FOR CREATE/UPDATE
  // ========================

  /**
   * Create event with formatted data helper
   * @param {Object} params - Event parameters
   * @returns {Promise<Object>} Created event
   */
  const createEventWithFormat = useCallback(async (params) => {
    validateAccess();

    // Ensure event_visible defaults to true if not provided
    const eventData = {
      event_visible: true,
      ...params,
    };

    // Format date if it's a Date object
    if (eventData.event_date instanceof Date) {
      eventData.event_date = eventData.event_date.toISOString().split('T')[0];
    }

    // Format time if needed (ensure HH:MM:SS format)
    if (eventData.event_time && eventData.event_time.length === 5) {
      eventData.event_time = `${eventData.event_time}:00`; // Add seconds if missing
    }

    return await createEvent(eventData);
  }, [createEvent, validateAccess]);

  /**
   * Update event status
   * @param {string} eventId - Event ID
   * @param {string} status - New status
   * @returns {Promise<Object>} Updated event
   */
  const updateEventStatus = useCallback(async (eventId, status) => {
    validateAccess();
    return await updateEvent(eventId, { event_status: status });
  }, [updateEvent, validateAccess]);

  /**
   * Update event visibility
   * @param {string} eventId - Event ID
   * @param {boolean} isVisible - Visibility status
   * @returns {Promise<Object>} Updated event
   */
  const updateEventVisibility = useCallback(async (eventId, isVisible) => {
    validateAccess();
    return await updateEvent(eventId, { event_visible: isVisible });
  }, [updateEvent, validateAccess]);

  /**
   * Cancel an event (sets status to 'cancelled')
   * @param {string} eventId - Event ID
   * @returns {Promise<Object>} Updated event
   */
  const cancelEvent = useCallback(async (eventId) => {
    validateAccess();
    return await updateEvent(eventId, { event_status: 'cancelled' });
  }, [updateEvent, validateAccess]);

  /**
   * Complete an event (sets status to 'completed')
   * @param {string} eventId - Event ID
   * @returns {Promise<Object>} Updated event
   */
  const completeEvent = useCallback(async (eventId) => {
    validateAccess();
    return await updateEvent(eventId, { event_status: 'completed' });
  }, [updateEvent, validateAccess]);

  /**
   * Reschedule an event
   * @param {string} eventId - Event ID
   * @param {string} newDate - New date (YYYY-MM-DD)
   * @param {string} newTime - New time (HH:MM:SS)
   * @returns {Promise<Object>} Updated event
   */
  const rescheduleEvent = useCallback(async (eventId, newDate, newTime) => {
    validateAccess();
    
    const updateData = {};
    if (newDate) updateData.event_date = newDate;
    if (newTime) updateData.event_time = newTime;
    
    return await updateEvent(eventId, updateData);
  }, [updateEvent, validateAccess]);

  /**
   * Update event pricing
   * @param {string} eventId - Event ID
   * @param {number} cost - Event cost
   * @param {number} price - Event price
   * @returns {Promise<Object>} Updated event
   */
  const updateEventPricing = useCallback(async (eventId, cost, price) => {
    validateAccess();
    
    const updateData = {};
    if (cost !== undefined && cost !== null) updateData.event_cost = cost;
    if (price !== undefined && price !== null) updateData.event_price = price;
    
    return await updateEvent(eventId, updateData);
  }, [updateEvent, validateAccess]);

  /**
   * Duplicate an event
   * @param {string} eventId - Event ID to duplicate
   * @param {Object} overrides - Fields to override in the duplicated event
   * @returns {Promise<Object>} Created duplicate event
   */
  const duplicateEvent = useCallback(async (eventId, overrides = {}) => {
    validateAccess();
    
    // Get the original event
    const originalEvent = await getEventById(eventId);
    
    // Create new event data based on original, excluding system fields
    const newEventData = {
      business_registry_number: originalEvent.business_registry_number,
      event_title: `${originalEvent.event_title} (Copy)`,
      event_date: originalEvent.event_date,
      event_time: originalEvent.event_time,
      event_description: originalEvent.event_description,
      event_status: 'scheduled', // Default to scheduled for duplicates
      event_guest_limit: originalEvent.event_guest_limit,
      event_cost: originalEvent.event_cost,
      event_price: originalEvent.event_price,
      event_location: originalEvent.event_location,
      event_location_latitude: originalEvent.event_location_latitude,
      event_location_longitude: originalEvent.event_location_longitude,
      event_instructions: originalEvent.event_instructions,
      location_id: originalEvent.location_id,
      client_id: originalEvent.client_id,
      employee_id: originalEvent.employee_id,
      product_id: originalEvent.product_id,
      service_id: originalEvent.service_id,
      event_visible: originalEvent.event_visible,
      ...overrides, // Apply any overrides
    };
    
    return await createEvent(newEventData);
  }, [getEventById, createEvent, validateAccess]);

  // ========================
  // BATCH OPERATIONS
  // ========================

  /**
   * Bulk update events
   * @param {Array<string>} eventIds - Array of event IDs
   * @param {Object} updateData - Data to update for all events
   * @returns {Promise<Array>} Array of update results
   */
  const bulkUpdateEvents = useCallback(async (eventIds, updateData) => {
    validateAccess();
    
    if (!eventIds || eventIds.length === 0) {
      throw new Error('Event IDs array is required');
    }

    setLoading(true);
    setError(null);

    try {
      const updatePromises = eventIds.map(eventId => 
        updateEvent(eventId, updateData)
      );
      
      const results = await Promise.allSettled(updatePromises);
      
      const successful = results.filter(r => r.status === 'fulfilled').map(r => r.value);
      const failed = results.filter(r => r.status === 'rejected').map((r, idx) => ({
        eventId: eventIds[idx],
        error: r.reason.message,
      }));
      
      return {
        successful,
        failed,
        totalProcessed: eventIds.length,
        successCount: successful.length,
        failureCount: failed.length,
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to bulk update events';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [updateEvent, validateAccess]);

  /**
   * Bulk delete events
   * @param {Array<string>} eventIds - Array of event IDs to delete
   * @returns {Promise<Object>} Deletion results
   */
  const bulkDeleteEvents = useCallback(async (eventIds) => {
    validateAccess();
    
    if (!eventIds || eventIds.length === 0) {
      throw new Error('Event IDs array is required');
    }

    setLoading(true);
    setError(null);

    try {
      const deletePromises = eventIds.map(eventId => deleteEvent(eventId));
      
      const results = await Promise.allSettled(deletePromises);
      
      const successful = results.filter(r => r.status === 'fulfilled').map(r => r.value);
      const failed = results.filter(r => r.status === 'rejected').map((r, idx) => ({
        eventId: eventIds[idx],
        error: r.reason.message,
      }));
      
      return {
        successful,
        failed,
        totalProcessed: eventIds.length,
        successCount: successful.length,
        failureCount: failed.length,
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to bulk delete events';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [deleteEvent, validateAccess]);

  // ========================
  // EXISTING FILTER/SEARCH METHODS
  // ========================

  // Get events filtered by status
  const getEventsByStatus = useCallback(async (registryNumber, status, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      eventStatus: status,
    };

    return await getEventsByRegistryNumber(registryNumber, filterOptions);
  }, [getEventsByRegistryNumber, validateAccess]);

  // Get events filtered by visibility
  const getEventsByVisibility = useCallback(async (registryNumber, isVisible, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      eventVisible: isVisible,
    };

    return await getEventsByRegistryNumber(registryNumber, filterOptions);
  }, [getEventsByRegistryNumber, validateAccess]);

  // Get events by date range
  const getEventsByDateRange = useCallback(async (registryNumber, dateFrom, dateTo, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      dateFrom: dateFrom,
      dateTo: dateTo,
    };

    return await getEventsByRegistryNumber(registryNumber, filterOptions);
  }, [getEventsByRegistryNumber, validateAccess]);

  // Get events by price range
  const getEventsByPriceRange = useCallback(async (registryNumber, minPrice, maxPrice, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      minPrice: minPrice,
      maxPrice: maxPrice,
    };

    return await getEventsByRegistryNumber(registryNumber, filterOptions);
  }, [getEventsByRegistryNumber, validateAccess]);

  // Search events
  const searchEvents = useCallback(async (registryNumber, searchTerm, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      search: searchTerm,
    };

    return await getEventsByRegistryNumber(registryNumber, filterOptions);
  }, [getEventsByRegistryNumber, validateAccess]);

  // Get upcoming events
  const getUpcomingEvents = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    
    const today = new Date().toISOString().split('T')[0];
    const filterOptions = {
      ...options,
      dateFrom: today,
    };

    return await getEventsByRegistryNumber(registryNumber, filterOptions);
  }, [getEventsByRegistryNumber, validateAccess]);

  // Get past events
  const getPastEvents = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    
    const today = new Date().toISOString().split('T')[0];
    const filterOptions = {
      ...options,
      dateTo: today,
    };

    return await getEventsByRegistryNumber(registryNumber, filterOptions);
  }, [getEventsByRegistryNumber, validateAccess]);

  // Get scheduled events
  const getScheduledEvents = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    return await getEventsByStatus(registryNumber, 'scheduled', options);
  }, [getEventsByStatus, validateAccess]);

  // Get events this month
  const getEventsThisMonth = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    
    const filterOptions = {
      ...options,
      dateFrom: firstDay,
      dateTo: lastDay,
    };

    return await getEventsByRegistryNumber(registryNumber, filterOptions);
  }, [getEventsByRegistryNumber, validateAccess]);

  // Get events with location
  const getEventsWithLocation = useCallback(async (registryNumber, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      hasLocation: true,
    };

    return await getEventsByRegistryNumber(registryNumber, filterOptions);
  }, [getEventsByRegistryNumber, validateAccess]);

  // Export events data (prepare data for CSV/Excel export)
  const exportEventsData = useCallback(async (registryNumber, filters = {}) => {
    validateAccess();

    // Get all events without pagination for export
    const exportOptions = {
      ...filters,
      limit: 10000, // Large limit for export
      offset: 0,
    };

    const result = await getEventsByRegistryNumber(registryNumber, exportOptions);
    
    // Transform data for export
    const exportData = result.events.map(event => ({
      'Event ID': event.event_id,
      'Event Title': event.event_title,
      'Date': new Date(event.event_date).toLocaleDateString(),
      'Time': event.event_time,
      'Description': event.event_description || '',
      'Status': event.event_status,
      'Guest Limit': event.event_guest_limit || 'No limit',
      'Cost': event.event_cost ? `$${event.event_cost.toFixed(2)}` : 'Free',
      'Price': event.event_price ? `$${event.event_price.toFixed(2)}` : 'Free',
      'Location': event.event_location || 'TBD',
      'Visible': event.event_visible ? 'Yes' : 'No',
      'Created': new Date(event.event_creation_date).toLocaleDateString(),
      'Last Updated': event.event_updated_date ? new Date(event.event_updated_date).toLocaleDateString() : '',
    }));

    return {
      data: exportData,
      totalCount: result.total_count,
      registryNumber: registryNumber,
      filename: `business_events_${registryNumber}_${new Date().toISOString().split('T')[0]}.csv`
    };
  }, [getEventsByRegistryNumber, validateAccess]);

  // Health check for business events service
  const checkHealth = useCallback(async () => {
    validateAccess();

    try {
      const healthUrl = loginType === 'master' 
        ? `${API_BASE_URL}/master/business-events/health`
        : `${API_BASE_URL}/admin/business-events/health`;
      
      const response = await makeAuthenticatedRequest(healthUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

  // ========================
  // VALIDATION HELPERS
  // ========================

  // Validate create event data
  const validateCreateEventData = useCallback((eventData) => {
    const errors = [];

    // Required fields
    if (!eventData.business_registry_number) {
      errors.push({ field: 'business_registry_number', message: 'Business registry number is required' });
    }
    if (!eventData.event_title || eventData.event_title.trim() === '') {
      errors.push({ field: 'event_title', message: 'Event title is required' });
    }
    if (!eventData.event_date) {
      errors.push({ field: 'event_date', message: 'Event date is required' });
    }
    if (!eventData.event_time) {
      errors.push({ field: 'event_time', message: 'Event time is required' });
    }
    if (!eventData.event_status) {
      errors.push({ field: 'event_status', message: 'Event status is required' });
    }

    // Date validation
    if (eventData.event_date) {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(eventData.event_date)) {
        errors.push({ field: 'event_date', message: 'Date must be in YYYY-MM-DD format' });
      }
    }

    // Time validation
    if (eventData.event_time) {
      const timeRegex = /^\d{2}:\d{2}(:\d{2})?$/;
      if (!timeRegex.test(eventData.event_time)) {
        errors.push({ field: 'event_time', message: 'Time must be in HH:MM or HH:MM:SS format' });
      }
    }

    // Price validation
    if (eventData.event_cost !== undefined && eventData.event_cost < 0) {
      errors.push({ field: 'event_cost', message: 'Event cost cannot be negative' });
    }
    if (eventData.event_price !== undefined && eventData.event_price < 0) {
      errors.push({ field: 'event_price', message: 'Event price cannot be negative' });
    }

    // Guest limit validation
    if (eventData.event_guest_limit !== undefined && eventData.event_guest_limit < 0) {
      errors.push({ field: 'event_guest_limit', message: 'Guest limit cannot be negative' });
    }

    // Location coordinates validation
    if (eventData.event_location_latitude !== undefined && 
        (eventData.event_location_latitude < -90 || eventData.event_location_latitude > 90)) {
      errors.push({ field: 'event_location_latitude', message: 'Latitude must be between -90 and 90' });
    }
    if (eventData.event_location_longitude !== undefined && 
        (eventData.event_location_longitude < -180 || eventData.event_location_longitude > 180)) {
      errors.push({ field: 'event_location_longitude', message: 'Longitude must be between -180 and 180' });
    }

    return errors;
  }, []);

  // Validate event filters
  const validateEventFilters = useCallback((filters) => {
    const errors = [];

    if (filters.limit && (filters.limit < 1 || filters.limit > 1000)) {
      errors.push({ field: 'limit', message: 'Limit must be between 1 and 1000' });
    }

    if (filters.offset && filters.offset < 0) {
      errors.push({ field: 'offset', message: 'Offset must be non-negative' });
    }

    if (filters.dateFrom && filters.dateTo && new Date(filters.dateFrom) > new Date(filters.dateTo)) {
      errors.push({ field: 'dateRange', message: 'Date from must be before date to' });
    }

    if (filters.minPrice && filters.minPrice < 0) {
      errors.push({ field: 'minPrice', message: 'Minimum price must be non-negative' });
    }

    if (filters.maxPrice && filters.maxPrice < 0) {
      errors.push({ field: 'maxPrice', message: 'Maximum price must be non-negative' });
    }

    if (filters.minPrice && filters.maxPrice && filters.minPrice > filters.maxPrice) {
      errors.push({ field: 'priceRange', message: 'Minimum price must be less than maximum price' });
    }

    if (filters.search && filters.search.length < 2) {
      errors.push({ field: 'search', message: 'Search term must be at least 2 characters' });
    }

    return errors;
  }, []);

  // Format event data for display
  const formatEventData = useCallback((event) => {
    const getEventDuration = () => {
      const eventDate = new Date(event.event_date);
      const now = new Date();
      const diffTime = eventDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) {
        return `${Math.abs(diffDays)} day${Math.abs(diffDays) > 1 ? 's' : ''} ago`;
      } else if (diffDays === 0) {
        return 'Today';
      } else if (diffDays === 1) {
        return 'Tomorrow';
      } else {
        return `In ${diffDays} day${diffDays > 1 ? 's' : ''}`;
      }
    };

    const formatDateTime = () => {
      const date = new Date(event.event_date);
      const formattedDate = date.toLocaleDateString();
      const formattedTime = event.event_time;
      return `${formattedDate} at ${formattedTime}`;
    };

    return {
      ...event,
      formattedDateTime: formatDateTime(),
      formattedDate: new Date(event.event_date).toLocaleDateString(),
      formattedTime: event.event_time,
      statusColor: event.event_status === 'scheduled' ? 'blue' :
                   event.event_status === 'completed' ? 'green' :
                   event.event_status === 'cancelled' ? 'red' :
                   event.event_status === 'postponed' ? 'orange' : 'gray',
      visibilityText: event.event_visible ? 'Visible' : 'Hidden',
      visibilityColor: event.event_visible ? 'green' : 'red',
      priceDisplay: event.event_price ? `$${event.event_price.toFixed(2)}` : 'Free',
      costDisplay: event.event_cost ? `$${event.event_cost.toFixed(2)}` : 'No cost',
      guestLimitText: event.event_guest_limit ? `${event.event_guest_limit} guests` : 'No limit',
      hasLocation: !!event.event_location,
      locationText: event.event_location || 'Location TBD',
      durationFromNow: getEventDuration(),
      isPastEvent: new Date(event.event_date) < new Date(),
      isUpcoming: new Date(event.event_date) >= new Date(),
      profitMargin: event.event_price && event.event_cost 
        ? ((event.event_price - event.event_cost) / event.event_price * 100).toFixed(1) + '%'
        : 'N/A',
      shortDescription: event.event_description && event.event_description.length > 150 
        ? event.event_description.substring(0, 150) + '...'
        : event.event_description,
    };
  }, []);

  // Get event summary for a business
  const getEventSummary = useCallback(async (registryNumber) => {
    validateAccess();

    try {
      const [stats, events] = await Promise.all([
        getEventStats(registryNumber),
        getEventsByRegistryNumber(registryNumber, { limit: 10 }) // Get sample events
      ]);

      return {
        registryNumber,
        totalEvents: stats.stats.total_events,
        visibleEvents: stats.stats.visible_events,
        hiddenEvents: stats.stats.hidden_events,
        upcomingEvents: stats.stats.upcoming_events,
        pastEvents: stats.stats.past_events,
        eventsThisMonth: stats.stats.events_this_month,
        eventsNextMonth: stats.stats.events_next_month,
        averageEventCost: stats.stats.average_event_cost,
        averageEventPrice: stats.stats.average_event_price,
        totalRevenue: stats.stats.total_revenue,
        eventsByStatus: stats.stats.events_by_status,
        sampleEvents: events.events,
        accessLevel: loginType,
        hasEvents: stats.stats.total_events > 0,
        visibilityRate: stats.stats.total_events > 0 
          ? (stats.stats.visible_events / stats.stats.total_events * 100).toFixed(1) + '%'
          : '0%',
        upcomingRate: stats.stats.total_events > 0 
          ? (stats.stats.upcoming_events / stats.stats.total_events * 100).toFixed(1) + '%'
          : '0%',
        averageProfitMargin: stats.stats.average_event_price > 0 && stats.stats.average_event_cost > 0
          ? ((stats.stats.average_event_price - stats.stats.average_event_cost) / stats.stats.average_event_price * 100).toFixed(1) + '%'
          : 'N/A',
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get event summary';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getEventStats, getEventsByRegistryNumber, validateAccess, loginType]);

  // Get event calendar data
  const getEventCalendarData = useCallback(async (registryNumber, year, month) => {
    validateAccess();

    try {
      const firstDay = new Date(year, month - 1, 1).toISOString().split('T')[0];
      const lastDay = new Date(year, month, 0).toISOString().split('T')[0];
      
      const result = await getEventsByDateRange(registryNumber, firstDay, lastDay, { limit: 1000 });
      
      // Group events by date
      const eventsByDate = {};
      result.events.forEach(event => {
        const dateKey = event.event_date;
        if (!eventsByDate[dateKey]) {
          eventsByDate[dateKey] = [];
        }
        eventsByDate[dateKey].push(event);
      });

      return {
        registryNumber,
        year,
        month,
        eventsByDate,
        totalEvents: result.total_count,
        daysWithEvents: Object.keys(eventsByDate).length,
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get calendar data';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getEventsByDateRange, validateAccess]);

  // Get current endpoint info for debugging
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: loginType === 'master' 
        ? `${API_BASE_URL}/master/business-events/api/v1/business-events`
        : `${API_BASE_URL}/admin/business-events/api/v1/business-events`,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master' 
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
      availableOperations: {
        create: 'POST /event',
        read: 'GET /event/{eventId}',
        update: 'PUT /event/{eventId}',
        delete: 'DELETE /event/{eventId}',
        list: 'GET /registry/{registryNumber}',
        stats: 'GET /registry/{registryNumber}/stats',
        statuses: 'GET /registry/{registryNumber}/statuses',
      },
    };
  }, [loginType, API_BASE_URL]);

  const value = {
    // State
    loading,
    error,
    
    // ===== CRUD Operations =====
    // Create
    createEvent,
    createEventWithFormat,
    duplicateEvent,
    
    // Read
    getEventsByRegistryNumber,
    getEventStats,
    getEventStatuses,
    getEventById,
    
    // Update
    updateEvent,
    updateEventStatus,
    updateEventVisibility,
    cancelEvent,
    completeEvent,
    rescheduleEvent,
    updateEventPricing,
    
    // Delete
    deleteEvent,
    
    // ===== Batch Operations =====
    bulkUpdateEvents,
    bulkDeleteEvents,
    
    // ===== Search and Filter Actions =====
    getEventsByStatus,
    getEventsByVisibility,
    getEventsByDateRange,
    getEventsByPriceRange,
    searchEvents,
    getUpcomingEvents,
    getPastEvents,
    getScheduledEvents,
    getEventsThisMonth,
    getEventsWithLocation,
    
    // ===== Utility Actions =====
    exportEventsData,
    checkHealth,
    getEventSummary,
    getEventCalendarData,
    
    // ===== Helpers =====
    clearError,
    validateEventFilters,
    validateCreateEventData,
    formatEventData,
    getEndpointInfo,
    
    // ===== Computed values =====
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasEventsAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <BusinessEventContext.Provider value={value}>
      {children}
    </BusinessEventContext.Provider>
  );
};